/**
 * Version checking utility to detect when a new app version is available
 */

let currentVersion = null;

/**
 * Load the current deployed version from embedded version or fallback to version.json
 * @returns {Promise<{version: string, buildTime: string}>}
 */
export async function fetchLatestVersion() {
    // First try to get version from embedded window variables (most reliable)
    if (window.APP_VERSION && window.APP_BUILD_TIME) {
        // Fetch version.js with cache busting to get the latest deployed version
        try {
            const script = document.createElement('script');
            const timestamp = Date.now();
            script.src = `/version.js?t=${timestamp}`;

            await new Promise((resolve, reject) => {
                script.onload = resolve;
                script.onerror = reject;
                document.head.appendChild(script);
            });

            // Remove the script after loading
            document.head.removeChild(script);

            if (window.APP_VERSION && window.APP_BUILD_TIME) {
                return {
                    version: window.APP_VERSION,
                    buildTime: window.APP_BUILD_TIME,
                };
            }
        } catch (error) {
            console.warn('Failed to load version.js, falling back to version.json');
        }
    }

    // Fallback to version.json
    try {
        const response = await fetch(`/version.json?t=${Date.now()}`, {
            cache: 'no-cache',
            headers: {
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache',
            },
        });

        if (!response.ok) {
            console.warn('Failed to fetch version.json');
            return null;
        }

        const versionData = await response.json();
        return versionData;
    } catch (error) {
        console.error('Error fetching version:', error);
        return null;
    }
}

/**
 * Initialize the current version from embedded build-time version
 * This should be called once when the app loads
 */
export async function initializeVersion() {
    // First try environment variable (embedded at build time - most reliable)
    if (import.meta.env.VITE_VERSION) {
        currentVersion = import.meta.env.VITE_VERSION;
        return currentVersion;
    }

    // Fallback to fetching version
    const versionData = await fetchLatestVersion();
    if (versionData) {
        currentVersion = versionData.version;
    }
    return currentVersion;
}

/**
 * Check if a new version is available
 * @returns {Promise<boolean>} true if a new version is available
 */
export async function checkForNewVersion() {
    if (!currentVersion) {
        // If we haven't initialized yet, do it now
        await initializeVersion();
        return false; // First load, no update needed
    }

    const latestVersion = await fetchLatestVersion();

    if (!latestVersion) {
        return false;
    }

    // Compare versions
    const isNewVersion = latestVersion.version !== currentVersion;

    if (isNewVersion) {
        console.log(`New version available: ${latestVersion.version} (current: ${currentVersion})`);
    }

    return isNewVersion;
}

/**
 * Force reload the application (clears cache)
 */
export function reloadApp() {
    // Clear any service worker cache if present
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
            registrations.forEach((registration) => registration.unregister());
        });
    }

    // Force hard reload
    window.location.reload(true);
}

/**
 * Get the current version string
 */
export function getCurrentVersion() {
    return currentVersion;
}
